import loadable from '@loadable/component';
import { ThemeProvider } from '@mui/material';
import i18next from 'i18next';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import BlockSkeletonUi from './components/block-skeleton-ui/BlockSkeletonUi';
import Notification from './components/notification/Notification';
import Routing from './components/routing/Routing';
import { useStream } from './hooks/useStream';
import {
    ConfirmDialogModeEnum,
    CustomerIdAndRoleAndLoginSettings,
    CustomerSettingsData,
    EditorModeEnum,
    LoginSettings,
    ToastTypeEnum,
} from './models/Index';
import CustomerContextService from './services/customerContextService/CustomerContextService';
import CustomerService from './services/customerService/CustomerService';
import SettingsService from './services/settingsService/SettingsService';
import StateService from './services/stateService/StateService';
import UserService from './services/userService/UserService';
import './styles.scss';
import customDarkTheme from './styles/customDarkTheme';
import customLightTheme from './styles/customLightTheme';
import './styles/grid-styles.css';
import './styles/resize-styles.css';

export const ConfirmDialog = loadable(() => import('./components/confirm-dialog/ConfirmDialog'));
export const Editor = loadable(() => import('./components/editor/Editor'));

function App() {
    const [customerSettings, setCustomerSettings] = useStream(StateService.customerSettings$);
    const [editorMode, setEditorMode] = useStream(StateService.editorMode$);
    const [isLoading, setIsLoading] = useState(true);
    const [t] = useTranslation();
    const [customerIdAndRoleAndLoginSettingsResponse, setCustomerIdAndRoleAndLoginSettingsResponse] = useState(
        {} as CustomerIdAndRoleAndLoginSettings,
    );
    const [confirmDialogMode, setConfirmDialogMode] = useStream(StateService.confirmDialogMode$);
    const [loginSettings, setLoginSettings] = useStream(StateService.loginSettings$);
    const abortController = new AbortController();
    const [theme, setTheme] = useState(null);
    const [themeName, setThemeName] = useStream(StateService.darkTheme$);
    const bodyTheme = localStorage.getItem('theme') || 'light'; // retrieve the value from local storage, default to 'light'

    useEffect(() => {
        // remove?
        setThemeName(theme ? theme : 'dark');
    }, [theme]);

    useEffect(() => {
        async function getData() {
            setIsLoading(true);
            await getCustomerIdAndRoleAndLoginSettings(earlyCustomerUuid);
        }

        // The tenant of the token is known before any request. A differing stored customer (superuser customer
        // switch) can only be validated once the roles are loaded, so the customer is resolved there.
        const tenantId = UserService.getTenantId();
        const storedCustomerUuid = CustomerContextService.getStoredCustomerUuid();
        const earlyCustomerUuid = tenantId && (!storedCustomerUuid || storedCustomerUuid === tenantId) ? tenantId : undefined;
        if (earlyCustomerUuid) {
            StateService.currentCustomerUuid$.next(earlyCustomerUuid);
            getCustomerSettings(earlyCustomerUuid, { signal: abortController.signal });
        }

        getData();

        return () => {
            abortController.abort();
        };
    }, []);

    useEffect(() => {
        if (customerIdAndRoleAndLoginSettingsResponse.roles?.length > 0) {
            StateService.currentRoles$.next(customerIdAndRoleAndLoginSettingsResponse.roles);
        } else if (customerIdAndRoleAndLoginSettingsResponse.roles?.length === 0) {
            StateService.toastMessage$.next({
                source: 'CUSTOMER_SERVICE.GET_CUSTOMER_ID_AND_ROLE',
                text: t('COMMON.NO_ROLE_ASSIGNED'),
                type: ToastTypeEnum.INFO,
            });
        }
        if (customerIdAndRoleAndLoginSettingsResponse.language?.length > 0) {
            const loginSettings = { language: customerIdAndRoleAndLoginSettingsResponse.language } as LoginSettings;
            StateService.loginSettings$.next(loginSettings);
        }
    }, [customerIdAndRoleAndLoginSettingsResponse]);

    useEffect(() => {
        if (loginSettings?.language && loginSettings?.language !== i18next.language) {
            i18next.changeLanguage(loginSettings.language);
        }
        // eslint-disable-next-line
    }, [loginSettings]);

    useEffect(() => {
        setTheme(bodyTheme === 'light' || undefined || null ? customLightTheme : customDarkTheme);
    }, [bodyTheme]);

    async function getCustomerIdAndRoleAndLoginSettings(earlyCustomerUuid?: string) {
        const response =
            (await CustomerService.getCustomerIdAndRoleAndLoginSettings(UserService.getUsername())) ||
            ({} as CustomerIdAndRoleAndLoginSettings);
        // resolved before the routes render, so no component starts with a customer that is replaced right after
        const customerUuid = CustomerContextService.resolveCustomerUuid(UserService.getTenantId() || response.customerUuid, response.roles);
        if (customerUuid?.length > 0 && customerUuid !== earlyCustomerUuid) {
            StateService.currentCustomerUuid$.next(customerUuid);
            getCustomerSettings(customerUuid, { signal: abortController.signal });
        }
        setIsLoading(false);
        setCustomerIdAndRoleAndLoginSettingsResponse(response);
    }

    async function getCustomerSettings(customerUuid: string, options?: any) {
        let response = (await SettingsService.getCustomerSettings(customerUuid, options)) as CustomerSettingsData;
        if (response && response.toastStyles) {
            StateService.customerSettings$.next(response);
        }
    }

    const getEditor = useMemo(() => {
        return <>{editorMode !== EditorModeEnum.CLOSED && <Editor />}</>;
    }, [editorMode]);

    const getConfirmDialog = useMemo(() => {
        return <>{confirmDialogMode !== ConfirmDialogModeEnum.CLOSED && <ConfirmDialog />}</>;
    }, [confirmDialogMode]);

    return (
        <ThemeProvider theme={theme ?? customLightTheme}>
            <Suspense
                fallback={
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <BlockSkeletonUi />
                    </div>
                }
            >
                <BrowserRouter>
                    {isLoading ? (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <BlockSkeletonUi />
                        </div>
                    ) : (
                        <>
                            <Routing />
                            {getEditor}
                            {getConfirmDialog}
                            <Notification notification$={StateService.toastMessage$} styles={customerSettings.toastStyles} />
                        </>
                    )}
                </BrowserRouter>
            </Suspense>
        </ThemeProvider>
    );
}

export default App;
